













































































import { Vue, Component } from 'vue-property-decorator'
import { StateChanger } from 'vue-infinite-loading'
import { ExStore } from 'vuex'
import { Patient } from '@/types/user'
import dayjs from 'dayjs'
import { isCordovaEnvironment } from '@/utils/cordova'
import { isCapacitorEnvironment } from '@/utils/capacitor'

interface HTMLAnchorElement {
  download: string
}

@Component({
  components: {
    ImageList: () => import('@/components/imageTab/ImageList.vue'),
    SyncImage: () => import('@/components/imageTab/SyncImage.vue'),
    ImageModal: () => import('@/components/imageTab/ImageModal.vue')
  }
})
export default class DicomIndex extends Vue {
  $store!: ExStore
  thumbnailUrls: {
    [id: number]: string
  } = {}
  selectedDicom: number | null = null
  selectedDicomUrl = ''
  patient: Patient | null = null

  get userInfo() {
    return this.$store.getters['user/userInfo']
  }

  get patientName() {
    if (!this.patient) return ''
    return `${this.patient.lastName}${this.patient.firstName}`
  }

  get patientId() {
    return Number(this.$route.params.patientId)
  }

  get dicoms() {
    return this.$store.getters['dicom/dicoms']
  }

  get hasImageNext() {
    return this.$store.getters['dicom/hasDicomNext']
  }

  get importantDicoms() {
    return this.$store.getters['dicom/importantDicoms']
  }

  get hasImportantImageNext() {
    return this.$store.getters['dicom/hasImportantDicomNext']
  }

  get selectedDicomInfo() {
    const dicom = this.getDicomFile(this.selectedDicom)
    if (!dicom) return {}
    const senderName = dicom.worker
      ? `${dicom.worker.lastName}${dicom.worker.occupation.name}`
      : this.patientName
    return {
      id: dicom.id,
      senderName,
      date: dayjs(dicom.registeredDatetime).format('M月D日 HH:mm'),
      description: dicom.description
    }
  }

  async created() {
    await this.fetchPatient()
    this.$store.dispatch('dicom/updateDicoms', { patientId: this.patientId })
    this.$store.dispatch('dicom/updateImportantDicoms', {
      patientId: this.patientId
    })
  }

  beforeDestroy() {
    for (const key in this.thumbnailUrls) {
      const url = this.thumbnailUrls[key]
      URL.revokeObjectURL(url)
    }
    URL.revokeObjectURL(this.selectedDicomUrl)
  }

  async fetchPatient() {
    this.patient = await this.$api.user.patient({ patientId: this.patientId })
  }

  handleEdit(dicomId: number) {
    this.$router.push({
      name: 'DicomTabEdit',
      params: { dicomId: String(dicomId) }
    })
  }

  async handleDownload(dicomId: number) {
    const dicom = this.getDicomFile(dicomId)
    if (!dicom) throw new Error('does not exist dicom file')
    if (isCordovaEnvironment() || isCapacitorEnvironment()) {
      this.$postMessage.openFileDownload({
        isPatient: this.userInfo.isPatient,
        isRelative: this.userInfo.isPatientRelative,
        type: 'dicom',
        patientId: this.patientId,
        fileId: dicomId,
        fileName: dicom.fileName || ''
      })
      return
    }
    const res = await this.$api.dicom.fetchDicom({
      patientId: this.patientId,
      dicomId
    })
    const url = window.URL.createObjectURL(res)
    const a = document.createElement('a')
    a.style.opacity = '0'
    document.body.appendChild(a)
    a.download = dicom.fileName || ''
    a.href = url
    a.click()
    a.remove()
    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 100)
  }

  async handleDelete(dicomId: number) {
    await this.$store.dispatch('dicom/removeDicom', {
      patientId: this.patientId,
      dicomId
    })
  }

  async handleUpdateImportantStatus({
    imageId,
    isImportant
  }: {
    imageId: number
    isImportant: boolean
  }) {
    await this.$store.dispatch('dicom/updateImportantStatus', {
      patientId: this.patientId,
      dicomId: imageId,
      isImportant
    })
  }

  getDicomFile(id: number | null) {
    if (!id) return
    return (
      this.dicoms.find(dicom => dicom.id === id) ||
      this.importantDicoms.find(dicom => dicom.id === id)
    )
  }

  getThumbnailUrl(dicomId: number) {
    return this.thumbnailUrls[dicomId] || ''
  }

  handleCloseModal() {
    this.selectedDicom = null
    this.selectedDicomUrl = ''
  }

  handleSelectImage(dicomId: number) {
    this.selectedDicom = dicomId
  }

  handleLoadNextImage($state: StateChanger) {
    this.$store
      .dispatch('dicom/loadNextDicoms', { patientId: this.patientId })
      .then(() => {
        if (this.hasImageNext) {
          $state.loaded()
        } else {
          $state.complete()
        }
      })
  }

  handleLoadNextImportantImage($state: StateChanger) {
    this.$store
      .dispatch('dicom/loadNextImportantDicoms', { patientId: this.patientId })
      .then(() => {
        if (this.hasImportantImageNext) {
          $state.loaded()
        } else {
          $state.complete()
        }
      })
  }

  async handleThumbnailCreated(dicomId: number) {
    if (dicomId in this.thumbnailUrls) return
    const res = await this.$api.dicom.fetchThumbnail({
      patientId: this.patientId,
      dicomId
    })
    const url = window.URL.createObjectURL(res)
    this.thumbnailUrls = { ...this.thumbnailUrls, [dicomId]: url }
  }

  async handleImageCreated(dicomId: number) {
    const res = await this.$api.dicom.fetchImage({
      patientId: this.patientId,
      dicomId
    })
    const url = window.URL.createObjectURL(res)
    this.selectedDicomUrl = url
  }
}
